import {
  Flex,
  FormField,
  FormFieldLabelPosition,
  Modal,
  PrimaryButton,
  Text,
  useAssistiveHideSiblings,
  useDisableBodyScroll,
  useFocusTrap,
  useInitialFocus,
  useModalModel,
  useReturnFocus
} from '@workday/canvas-kit-react'
import React, { useState } from 'react'
import { MapLayerModel } from '../../models/MapLayerModel'
import { Centered } from '../../components'
import styled from '@emotion/styled'
import { arrowLeftIcon, arrowRightIcon } from '@workday/canvas-system-icons-web'
import AutoRefreshLine from './AutoRefreshLine'

const StyledModalOverlay = styled(Modal.Overlay)`
  >div {
    align-items: center!important;
  }
`
const MediaWrapper = styled.div`
  position: relative;
`
const ImageAttachment = styled.img`
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
`
const VideoAttachment = styled.video`
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;

  &::-webkit-media-controls-closed-captions-button {
    display: none;
  }
  
  &::-webkit-media-controls-closed-captions-track-list {
    display: none;
  }
`
const NavButton = styled(PrimaryButton)<{isLeft: boolean}>`
  position: absolute;
  top: 50%;
  ${(props) => props.isLeft ? 'left: 10px;' : 'right: 10px;' }
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  padding: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
`

export interface MapLayerModalProps {
  onHide: () => void
  item: MapLayerModel
  isSmallScreen: boolean
}

const MapLayerModal = ({item, onHide, isSmallScreen}: MapLayerModalProps) => {
  const model = useModalModel({})
  useInitialFocus(model)
  useReturnFocus(model)
  useFocusTrap(model)
  useAssistiveHideSiblings(model)
  useDisableBodyScroll(model)
  model.state.visibility = 'visible'

  const [imageKey, setImageKey] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0)
  const currentMedia = item.attachments[currentIndex]
  const showPager = item.attachments.length > 1

  React.useEffect(() => {
    if (!showPager) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious()
      } else if (e.key === 'ArrowRight') {
        handleNext()
      } else if (e.key === 'Escape') {
        onHide()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showPager])

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : item.attachments.length - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < item.attachments.length - 1 ? prev + 1 : 0))
  }

  return (
    <Modal model={model}>
      <StyledModalOverlay>
        <Modal.Card width={isSmallScreen ? '95vw' : '45vw'}>
          <Modal.CloseIcon aria-label="Close" onClick={onHide} />
          <Modal.Heading>{item.itemName}</Modal.Heading>
          <Modal.Body>
            {currentMedia && (
              <div style={{margin: '15px 0px'}}>
                <MediaWrapper>
                  {currentMedia.isImage() ? (
                    <Centered>
                      <ImageAttachment key={imageKey} src={`${currentMedia.getURL()}?t=${imageKey}`} />
                    </Centered>
                  ) : currentMedia.isVideo() && (
                    <Centered>
                      <VideoAttachment src={currentMedia.getURL()} controls autoPlay>
                        Your browser does not support the video tag.
                      </VideoAttachment>
                    </Centered>
                  )}
                  {showPager && (
                    <>
                      {currentIndex > 0 && (
                        <NavButton isLeft={true} onClick={handlePrevious} icon={arrowLeftIcon} />
                      )}
                      {currentIndex + 1 < item.attachments.length && (
                        <NavButton isLeft={false}  onClick={handleNext} icon={arrowRightIcon} />
                      )}
                    </>
                  )}
                </MediaWrapper>
                {currentMedia.isImage() && currentMedia.refresh ? (
                  <Flex>
                    <Flex.Item flex={1}>
                      <AutoRefreshLine 
                        onRefresh={(completion) => {
                          setImageKey(prev => prev + 1)
                          completion()
                        }} 
                        refreshIntervalSeconds={currentMedia.refresh}
                        isSmallScreenWidth />
                    </Flex.Item>
                  </Flex>
                ) : (<></>)}
              </div>
            )}
            {item.info && Object.entries(item.info).map(([key, value]) => 
              (
                <FormField label={key} key={key} labelPosition={FormFieldLabelPosition.Top}>
                  <Text style={{ whiteSpace: 'pre-line' }} typeLevel="body.small">{value}</Text>
                </FormField>
              )
            )}
          </Modal.Body>
        </Modal.Card>
      </StyledModalOverlay>
    </Modal>
  )
}

export default MapLayerModal
